/**
 * AES-256-CBC + PKCS7, payload `{ iv, value }` as JSON string.
 * Must match Laravel: openssl_decrypt(..., 'AES-256-CBC', $secretKey, OPENSSL_RAW_DATA, $iv).
 * Key: exactly 32 UTF-8 bytes (REACT_APP_STORAGE_SECRET).
 */
import CryptoJS from 'crypto-js';

function getKey(): CryptoJS.lib.WordArray | null {
  const raw = process.env.REACT_APP_STORAGE_SECRET;
  if (!raw) return null;
  const key = CryptoJS.enc.Utf8.parse(raw);
  if (key.sigBytes !== 32) return null;
  return key;
}

/** Call before patching storage; avoids patching when misconfigured. */
export function isStorageCryptoConfigured(): boolean {
  return getKey() !== null;
}

export function encryptString(plain: string): string | null {
  const key = getKey();
  if (!key) return null;

  const iv = CryptoJS.lib.WordArray.random(16);
  const encrypted = CryptoJS.AES.encrypt(plain, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return JSON.stringify({
    iv: CryptoJS.enc.Base64.stringify(iv),
    value: encrypted.ciphertext.toString(CryptoJS.enc.Base64),
  });
}

export function decryptString(stored: string | null): string | null {
  if (stored == null) return null;

  const trimmed = stored.trim();
  if (!trimmed.startsWith('{')) return stored;

  let payload: { iv?: string; value?: string };
  try {
    payload = JSON.parse(trimmed);
  } catch {
    return stored;
  }
  if (typeof payload.iv !== 'string' || typeof payload.value !== 'string') {
    return stored;
  }

  const key = getKey();
  if (!key) return stored;

  try {
    const iv = CryptoJS.enc.Base64.parse(payload.iv);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(payload.value),
    });
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const str = decrypted.toString(CryptoJS.enc.Utf8);
    return str.length ? str : null;
  } catch {
    return stored;
  }
}

export function encryptData(data: unknown): string | null {
  return encryptString(JSON.stringify(data));
}

export function decryptData<T = unknown>(stored: string | null): T | null {
  const s = decryptString(stored);
  if (s == null) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}
